/**
 * B2BDeliveryNotes — Phase 2D
 *
 * Composant admin : liste des BL d'un dossier + bouton de création + actions.
 *
 * À intégrer dans OrderDetail (B2BTab.jsx) :
 *
 *   <B2BDeliveryNotesSection orderId={order.id} order={order} canWrite={canWrite} />
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Plus, Loader2, X, Trash2, Download, Copy, CheckCircle2,
  Sparkles,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` });

const fmtFCFA = (n) => (n == null ? '—' : `${Math.round(n).toLocaleString('fr-FR')} FCFA`);

// ─── Modale de création de BL ────────────────────────────────────────────

const CreateDeliveryNoteModal = ({ order, onClose, onCreated }) => {
  const [items, setItems] = useState(() =>
    (order?.items || []).map((it) => ({
      designation: it.designation || '',
      quantity: it.quantity || 1,
      unit: it.unit || '',
      code: it.code || '',
      unit_price: it.unit_price || 0,
    })) || [{ designation: '', quantity: 1, unit: '', code: '', unit_price: 0 }]
  );
  const [deliveryDate, setDeliveryDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const addRow = () => setItems((a) => [...a, { designation: '', quantity: 1, unit: '', code: '', unit_price: 0 }]);
  const upd = (i, k, v) => setItems((a) => a.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const del = (i) => setItems((a) => a.filter((_, idx) => idx !== i));
  const total = items.reduce((s, x) => s + (x.quantity || 0) * (x.unit_price || 0), 0);

  const submit = async () => {
    const valid = items.filter((x) => x.designation?.trim());
    if (valid.length === 0) { toast.error('Au moins une ligne avec désignation est requise.'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/b2b/orders/${order.id}/delivery-notes`, {
        items: valid,
        delivery_date: deliveryDate,
        notes: notes || null,
      }, { headers: auth() });
      toast.success(`BL créé · BL-${r.data.id.slice(0, 8).toUpperCase()}`);
      onCreated?.(r.data);
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erreur lors de la création');
    } finally { setSaving(false); }
  };

  if (items.length === 0) {
    setItems([{ designation: '', quantity: 1, unit: '', code: '', unit_price: 0 }]);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="dn-create-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[calc(100dvh-80px)] flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-[#2E7D32] to-[#1B5E20] px-5 py-3 text-white flex items-center justify-between">
          <h2 className="font-semibold flex items-center gap-2"><FileText className="w-5 h-5" /> Nouveau Bon de Livraison</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10" data-testid="dn-create-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 overflow-y-auto space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-zinc-600">Date de livraison</Label>
              <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} data-testid="dn-delivery-date" />
            </div>
            <div className="flex items-end">
              <p className="text-sm text-zinc-600">
                Client : <span className="font-semibold text-zinc-900">{order.clinic_name}</span>
              </p>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <Label className="text-sm font-medium">Articles livrés *</Label>
              <Button type="button" size="sm" variant="outline" onClick={addRow} data-testid="dn-add-row">
                <Plus className="w-3 h-3 mr-1" /> Ligne
              </Button>
            </div>
            <div className="space-y-1.5">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 items-center p-1.5 bg-zinc-50 rounded">
                  <Input className="col-span-5 text-sm h-9" placeholder="Désignation *" value={it.designation} onChange={(e) => upd(i, 'designation', e.target.value)} data-testid={`dn-item-designation-${i}`} />
                  <Input className="col-span-1 text-sm h-9" type="number" min="0" step="0.5" placeholder="Qté" value={it.quantity} onChange={(e) => upd(i, 'quantity', parseFloat(e.target.value) || 0)} data-testid={`dn-item-qty-${i}`} />
                  <Input className="col-span-1 text-sm h-9" placeholder="Unité" value={it.unit} onChange={(e) => upd(i, 'unit', e.target.value)} />
                  <Input className="col-span-2 text-sm h-9" placeholder="Code" value={it.code} onChange={(e) => upd(i, 'code', e.target.value)} />
                  <Input className="col-span-2 text-sm h-9" type="number" min="0" placeholder="PU TTC" value={it.unit_price} onChange={(e) => upd(i, 'unit_price', parseFloat(e.target.value) || 0)} data-testid={`dn-item-price-${i}`} />
                  <Button variant="ghost" size="icon" className="col-span-1 h-8 w-8" onClick={() => del(i)} data-testid={`dn-item-del-${i}`}>
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="text-right text-sm font-semibold text-zinc-800">
            Total HT/TTC : <span className="text-emerald-700">{fmtFCFA(total)}</span>
          </div>

          <div>
            <Label className="text-xs font-medium text-zinc-600">Notes (optionnel)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex : Livraison après-midi, point de retrait pharmacie." data-testid="dn-notes" />
          </div>
        </div>

        <div className="shrink-0 border-t border-zinc-200 bg-white px-4 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dn-create-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
            Créer le BL
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Bouton "Créer BL depuis PDF facture" ────────────────────────────────

const FromPdfButton = ({ orderId, onCreated }) => {
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('PDF requis.');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/b2b/orders/${orderId}/delivery-notes/from-pdf`, fd, {
        headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
      });
      const items = r.data.items?.length || 0;
      toast.success(`BL créé automatiquement · ${items} ligne(s) extraite(s)`);
      onCreated?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : "Impossible d'extraire les lignes");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };
  return (
    <>
      <input ref={fileRef} type="file" accept=".pdf" onChange={upload} className="hidden" data-testid="dn-from-pdf-input" />
      <Button
        size="sm"
        variant="outline"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="border-violet-300 text-violet-700 hover:bg-violet-50"
        data-testid="dn-from-pdf-btn"
      >
        {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
        Depuis facture PDF
      </Button>
    </>
  );
};

// ─── Section BL pour un dossier ──────────────────────────────────────────

export const B2BDeliveryNotesSection = ({ order, canWrite, canDelete }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/b2b/orders/${order.id}/delivery-notes`, { headers: auth() });
      setList(r.data || []);
    } catch (_) {
      setList([]);
    }
    setLoading(false);
  }, [order.id]);

  useEffect(() => { reload(); }, [reload]);

  const copyPublicLink = (token) => {
    const url = `${window.location.origin}/bl/${token}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success('Lien copié — envoyez-le à la clinique'),
      () => toast.info(url),
    );
  };

  const openPdf = async (id) => {
    try {
      const r = await axios.get(`${API}/b2b/delivery-notes/${id}/pdf`, {
        headers: auth(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
    } catch (_) {
      toast.error('Impossible d\'ouvrir le PDF');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer définitivement ce bon de livraison ?')) return;
    try {
      await axios.delete(`${API}/b2b/delivery-notes/${id}`, { headers: auth() });
      toast.success('BL supprimé');
      reload();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-3" data-testid="b2b-delivery-notes-section">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-800 flex items-center gap-1.5">
          <FileText className="w-4 h-4 text-zinc-500" /> Bons de livraison
          {list.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600 text-[10px] font-semibold">
              {list.length}
            </span>
          )}
        </h3>
        {canWrite && (
          <div className="flex gap-1.5">
            <FromPdfButton orderId={order.id} onCreated={reload} />
            <Button size="sm" onClick={() => setCreating(true)} className="bg-[#2E7D32] hover:bg-[#1B5E20] text-white" data-testid="dn-create-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Manuel
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="p-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-zinc-400" /></div>
      ) : list.length === 0 ? (
        <p className="text-xs text-zinc-500 italic p-3 bg-zinc-50 rounded-lg">
          Aucun BL pour ce dossier. {canWrite && 'Créez le premier en cliquant sur « Nouveau BL ».'}
        </p>
      ) : (
        <div className="space-y-2">
          {list.map((dn) => (
            <div key={dn.id} className="border border-zinc-200 rounded-lg p-3 bg-white" data-testid={`dn-row-${dn.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold text-zinc-900">
                      BL-{dn.id.slice(0, 8).toUpperCase()}
                    </span>
                    {dn.status === 'signed' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold">
                        <CheckCircle2 className="w-3 h-3" /> Signé
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold">
                        En attente signature
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-600 mt-0.5">
                    Livraison du {dn.delivery_date} · {dn.items?.length || 0} ligne(s) · {fmtFCFA(dn.total_amount)}
                  </p>
                  {dn.signed_by_name && (
                    <p className="text-xs text-emerald-700 mt-0.5">
                      ✍️ Signé par {dn.signed_by_name}
                      {dn.signed_by_role ? ` (${dn.signed_by_role})` : ''}
                      {dn.signed_at && ` · ${new Date(dn.signed_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}`}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-1.5 mt-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => openPdf(dn.id)} data-testid={`dn-pdf-${dn.id}`}>
                  <Download className="w-3 h-3 mr-1" /> PDF
                </Button>
                {dn.status !== 'signed' && (
                  <Button size="sm" variant="outline" onClick={() => copyPublicLink(dn.public_token)} data-testid={`dn-copy-${dn.id}`}>
                    <Copy className="w-3 h-3 mr-1" /> Lien clinique
                  </Button>
                )}
                {canDelete && (
                  <Button size="sm" variant="ghost" onClick={() => del(dn.id)} className="text-red-600 hover:bg-red-50 ml-auto" data-testid={`dn-del-${dn.id}`}>
                    <Trash2 className="w-3 h-3 mr-1" /> Supprimer
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <CreateDeliveryNoteModal
          order={order}
          onClose={() => setCreating(false)}
          onCreated={() => reload()}
        />
      )}
    </div>
  );
};

export default B2BDeliveryNotesSection;
